import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import Sidebar from "./Sidebar";
import api from "../../utils/api";
import {
  confirmAlert,
  successAlert,
  errorAlert,
  loadingAlert,
  closeAlert,
} from "../../utils/swalConfig";

const BankManagement = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankForm, setBankForm] = useState({
    bankName: "",
    code: "",
    isAdminBank: false,
    isNigerianBank: true,
    active: true,
    accountName: "", // For admin banks
    accountNumber: "", // For admin banks
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchBanks = async () => {
    try {
      loadingAlert("Loading banks...");
      const token = localStorage.getItem("jwt");
      const response = await api.get("/admin/banks", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        setBanks(response.data.data.banks);
        closeAlert();
      }
    } catch (err) {
      closeAlert();
      console.error("Failed to fetch banks:", err);
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to fetch banks"
      );
      setError(err.response?.data?.message || "Failed to fetch banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredBanks = banks.filter((bank) => {
    if (!searchTerm) return true;
    return bank?.bankName?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedBanks = filteredBanks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCreate = async () => {
    try {
      setSubmitting(true);
      loadingAlert("Creating bank...");
      const token = localStorage.getItem("jwt");
      const response = await api.post("/admin/banks", bankForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      await successAlert("Success!", "Bank created successfully");
      setBanks([...banks, response.data.data.bank]);
      setOpen(false);
      resetBankForm();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to create bank"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSubmitting(true);
      loadingAlert("Updating bank...");
      const token = localStorage.getItem("jwt");
      const response = await api.patch(
        `/admin/banks/${selectedBank._id}`,
        bankForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      closeAlert();
      await successAlert("Success!", "Bank updated successfully");
      setBanks(
        banks.map((bank) =>
          bank._id === selectedBank._id ? response.data.data.bank : bank
        )
      );
      setOpen(false);
      resetBankForm();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to update bank"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bankId) => {
    try {
      const result = await confirmAlert(
        "Delete Bank",
        "Are you sure you want to delete this bank?",
        "warning"
      );

      if (!result.isConfirmed) return;

      loadingAlert("Deleting bank...");
      const token = localStorage.getItem("jwt");
      await api.delete(`/admin/banks/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      await successAlert("Success!", "Bank deleted successfully");
      setBanks(banks.filter((bank) => bank._id !== bankId));
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to delete bank"
      );
    }
  };

  const handleAddBank = async (formData) => {
    try {
      loadingAlert("Adding bank...");
      const token = localStorage.getItem("jwt");
      await api.post("/admin/banks", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      await successAlert("Success!", "Bank has been added successfully.");
      fetchBanks(); // Refresh bank list
    } catch (err) {
      closeAlert();
      errorAlert("Error!", err.response?.data?.message || "Failed to add bank");
    }
  };

  const handleDeleteBank = async (bankId) => {
    try {
      const result = await confirmAlert(
        "Delete Bank",
        "Are you sure you want to delete this bank?",
        "warning"
      );

      if (result.isConfirmed) {
        loadingAlert("Deleting bank...");
        const token = localStorage.getItem("jwt");
        await api.delete(`/admin/banks/${bankId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        closeAlert();
        await successAlert("Deleted!", "Bank has been deleted successfully.");
        fetchBanks(); // Refresh bank list
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to delete bank"
      );
    }
  };

  // Add a helper function to reset the bank form
  const resetBankForm = () => {
    setBankForm({
      bankName: "",
      code: "",
      isAdminBank: false,
      isNigerianBank: true,
      active: true,
      accountName: "",
      accountNumber: "",
    });
    setSelectedBank(null);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: { xs: 8, sm: 9 }, // Add margin top to account for AppBar
          backgroundColor: "#f5f5f5",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Bank Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedBank(null);
              setBankForm({
                bankName: "",
                code: "",
                isAdminBank: false,
                isNigerianBank: true,
                active: true,
                accountName: "", // For admin banks
                accountNumber: "", // For admin banks
              });
              setOpen(true);
            }}
          >
            Add Bank
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search banks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Bank Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : !banks.length ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No banks found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBanks.map((bank) => (
                  <TableRow
                    key={bank._id}
                    sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                  >
                    <TableCell>{bank.bankName}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          bank.isNigerianBank
                            ? "Nigerian Bank"
                            : "International"
                        }
                        color={bank.isNigerianBank ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bank.active ? "Active" : "Inactive"}
                        color={bank.active ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Edit Bank">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBank(bank);
                              setBankForm(bank);
                              setOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Bank">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(bank._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredBanks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedBank ? "Edit Bank" : "Add New Bank"}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Bank Name"
              value={bankForm.bankName}
              onChange={(e) =>
                setBankForm({ ...bankForm, bankName: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Bank Code"
              value={bankForm.code}
              onChange={(e) =>
                setBankForm({ ...bankForm, code: e.target.value })
              }
              helperText="Unique bank identifier code (e.g. 044 for Access Bank)"
              margin="normal"
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={bankForm.isAdminBank}
                  onChange={(e) =>
                    setBankForm({ ...bankForm, isAdminBank: e.target.checked })
                  }
                />
              }
              label="Admin Bank"
            />
            {bankForm.isAdminBank && (
              <>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={bankForm.accountName}
                  onChange={(e) =>
                    setBankForm({ ...bankForm, accountName: e.target.value })
                  }
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Account Number"
                  value={bankForm.accountNumber}
                  onChange={(e) =>
                    setBankForm({ ...bankForm, accountNumber: e.target.value })
                  }
                  margin="normal"
                  required
                />
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={bankForm.isNigerianBank}
                  onChange={(e) =>
                    setBankForm({
                      ...bankForm,
                      isNigerianBank: e.target.checked,
                    })
                  }
                />
              }
              label="Nigerian Bank"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={selectedBank ? handleUpdate : handleCreate}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : selectedBank ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default BankManagement;
